import type { ColumnPinningPosition, TableState } from '@tanstack/react-table';

import type { CharlieTableProps } from '../../charlie-table.types';

export interface ColumnMetadata {
	id: string;
	canSort: boolean;
	canHide: boolean;
	isSorted: false | 'asc' | 'desc';
	isPinned: false | 'left' | 'right';
	index: number;
	isFirstColumn: (position?: ColumnPinningPosition | 'center') => boolean;
	isLastColumn: (position?: ColumnPinningPosition | 'center') => boolean;
}

// util function to splice and reorder the columnOrder array. Tanstack unfortunately does not provide a good helper for this, so we need to do it manually.
export const reorderColumn = ({
	movingColumnId,
	targetDistance,
	columnOrder,
	columnVisibility,
	columnPinning,
}: {
	movingColumnId: string;
	targetDistance: number | 'first' | 'last';
	columnOrder: string[];
	columnVisibility: Record<string, boolean> | undefined;
	columnPinning?: {
		left?: string[];
		right?: string[];
	};
}): { columnOrder: string[]; columnPinning?: { left?: string[]; right?: string[] } } => {
	const isLeftPinned = columnPinning?.left?.includes(movingColumnId);

	// Handle left-pinned column reordering
	if (isLeftPinned && typeof targetDistance === 'number' && columnPinning?.left) {
		const leftPinned = [...columnPinning.left];
		const currentIndex = leftPinned.indexOf(movingColumnId);

		if (currentIndex < 0) {
			return { columnOrder, columnPinning };
		}

		const targetIndex = currentIndex + targetDistance;

		if (targetIndex < 0 || targetIndex >= leftPinned.length) {
			return { columnOrder, columnPinning };
		}

		// Reorder the pinned array
		const [removed] = leftPinned.splice(currentIndex, 1);
		leftPinned.splice(targetIndex, 0, removed);

		// Also reorder columnOrder to keep body columns aligned
		const newColumnOrder = [...columnOrder];
		const swapWithId = columnOrder[targetIndex];
		const orderSourceIndex = newColumnOrder.indexOf(movingColumnId);
		const orderTargetIndex = newColumnOrder.indexOf(swapWithId);

		if (orderSourceIndex >= 0 && orderTargetIndex >= 0) {
			[newColumnOrder[orderSourceIndex], newColumnOrder[orderTargetIndex]] = [
				newColumnOrder[orderTargetIndex],
				newColumnOrder[orderSourceIndex],
			];
		}

		return {
			columnOrder: newColumnOrder,
			columnPinning: {
				...columnPinning,
				left: leftPinned,
			},
		};
	}

	/**
	 * Column order is affected by column visibility and column pinning.
	 * So if we're moving unpinned columns, we need to filter out the pinned columns and only use the visible columns.
	 */
	const visibleColumnOrder = columnOrder.filter(
		(id) =>
			columnVisibility?.[id] !== false &&
			!columnPinning?.left?.includes(id) &&
			!columnPinning?.right?.includes(id),
	);

	const newColumnOrder = [...columnOrder];

	// Remove the moving column from its current position first
	const currentIndex = newColumnOrder.indexOf(movingColumnId);
	if (currentIndex !== -1) {
		newColumnOrder.splice(currentIndex, 1);
	}

	if (targetDistance === 'first') {
		newColumnOrder.splice(0, 0, movingColumnId);
	} else if (targetDistance === 'last') {
		newColumnOrder.splice(newColumnOrder.length, 0, movingColumnId);
	} else {
		const targetVisibleIndex = visibleColumnOrder.indexOf(movingColumnId) + targetDistance;
		const targetColumn = visibleColumnOrder[targetVisibleIndex];
		const targetIndex = columnOrder.indexOf(targetColumn);
		newColumnOrder.splice(targetIndex, 0, movingColumnId);
	}
	return { columnOrder: newColumnOrder, columnPinning };
};

// util function to see if a column can be moved left and/or right
export const columnMoveOptions = ({
	column,
	tableState,
}: {
	column: ColumnMetadata;
	tableState: Pick<TableState, 'columnOrder' | 'columnPinning' | 'columnVisibility'>;
}): Set<'left' | 'right'> => {
	const columnOrder = tableState.columnOrder;
	const canMove = new Set<'left' | 'right'>();

	if (!columnOrder.includes(column.id)) {
		return new Set();
	}

	const isLeftPinned = tableState.columnPinning?.left?.includes(column.id);
	const leftPinnedColumns = tableState.columnPinning?.left || [];

	if (isLeftPinned) {
		const leftPinnedIndex = leftPinnedColumns.indexOf(column.id);

		// Can move left if not the leftmost left-pinned column
		if (leftPinnedIndex > 0) {
			canMove.add('left');
		}

		// Can move right if not the rightmost left-pinned column
		if (leftPinnedIndex < leftPinnedColumns.length - 1) {
			canMove.add('right');
		}

		return canMove;
	}

	if (!column.isPinned) {
		canMove.add('left');
		canMove.add('right');

		const visibleColumnOrder = columnOrder.filter(
			(id) => tableState.columnVisibility?.[id] !== false,
		);

		const firstUnpinnedColumnIndex = visibleColumnOrder.findIndex(
			(id) => !leftPinnedColumns.includes(id) && !tableState.columnPinning?.right?.includes(id),
		);

		const currentColumnIndex = visibleColumnOrder.indexOf(column.id);

		if (currentColumnIndex === firstUnpinnedColumnIndex) {
			canMove.delete('left');
		}

		const isLastColumn =
			column.isLastColumn('center') || columnOrder[columnOrder.length - 1] === column.id;
		if (isLastColumn) {
			canMove.delete('right');
		}

		return canMove;
	}
	return new Set();
};

// util function to get the icon props for an action
export const getIconPropsForAction = <T extends object>({
	columnActions,
	action,
}: {
	columnActions: NonNullable<CharlieTableProps<T>['columnActions']>;
	action: keyof NonNullable<CharlieTableProps<T>['columnActions']>;
}) => {
	const actionConfig = columnActions[action];
	const hasIcons = actionConfig && typeof actionConfig === 'object';

	return {
		iconBefore: hasIcons ? actionConfig.iconBefore : undefined,
		iconAfter: hasIcons ? actionConfig.iconAfter : undefined,
	};
};
